import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useGetInterventionInputsQuery } from '../../store/is/interventionInputApi'
import {
  InterventionInput,
  UserGroup
} from '../../types'
import { Badge } from "../ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table"
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs"
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible"

// Mapping between model types and display names
const inputTypeLabels: Record<string, string> = {
  'conventional_fertilizer': 'Fertilizer (Conventional)',
  'low_carbon_fertilizer': 'Low Carbon Fertilizer',
  'nitrogen_stabilized_fertilizer': 'Nitrogen Stabilized Fertilizer',
  'controlled_release_fertilizer': 'Controlled Release Fertilizer',
  'fuel': 'Fuel'
}

interface InterventionInputTableProps {
  onRowClick: (input: InterventionInput) => void;
}

const InterventionInputTable: React.FC<InterventionInputTableProps> = ({ onRowClick }) => {
  const [selectedInputType, setSelectedInputType] = useState<string>('all')
  const { data: apiInputs = [], isLoading } = useGetInterventionInputsQuery()
  
  // Track which sections are open (default all open)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  
  // Use mock data if API data is empty
  const inputs = apiInputs  // apiInputs.length > 0 ? apiInputs : mockInputs

  const userGroups = useSelector((state: any) => 
    state.auth.groups?.map((group: any) => group?.name)
  ) || []

  const hasEditPermission = userGroups.some(
    (group: string) => group === UserGroup.PROBA_OPERATOR || group === UserGroup.ADMIN
  )

  // Get unique input types from the data
  const inputTypes = Array.from(new Set(inputs.map(input => input.input_type)))

  // Filter inputs based on selected type
  const filteredInputs = selectedInputType === 'all' 
    ? inputs 
    : inputs.filter(input => input.input_type === selectedInputType)

  // Group inputs by type for table sections
  const groupedInputs = filteredInputs.reduce((acc, input) => {
    const type = input.input_type
    if (!acc[type]) {
      acc[type] = []
    }
    acc[type].push(input)
    return acc
  }, {} as Record<string, InterventionInput[]>)

  // Initialize open sections if not set
  React.useEffect(() => {
    if (Object.keys(openSections).length === 0) {
      const initialOpenState = inputTypes.reduce((acc, type) => {
        acc[type] = true // Default all sections to open
        return acc
      }, {} as Record<string, boolean>)
      setOpenSections(initialOpenState)
    }
  }, [inputTypes])

  // Toggle section open/closed
  const toggleSection = (type: string) => {
    setOpenSections(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  // Common columns for all input types - always display these
  const renderCommonColumns = (input: InterventionInput) => (
    <>
      <TableCell className="font-medium">{input.input_code}</TableCell>
      <TableCell className="font-medium">{input.input_name}</TableCell>
      <TableCell>{input.emission_factor.toFixed(4)}</TableCell>
      <TableCell>{input.emission_factor_unit}</TableCell>
      <TableCell className="max-w-[200px] truncate" title={input.input_source}>
        {input.input_source}
      </TableCell>
      <TableCell>{input.year}</TableCell>
      <TableCell>{input.region}</TableCell>
      <TableCell>
        <Badge variant={input.status === 'active' ? 'default' : 'destructive'}>
          {input.status}
        </Badge>
      </TableCell>
    </>
  )

  // Type-specific columns based on input type - additional columns only for specific types
  const renderTypeSpecificColumns = (input: InterventionInput) => {
    switch(input.input_type) {
      case 'low_carbon_fertilizer':
      case 'conventional_fertilizer':
        const fertilizerInput = input as any
        return (
          <>
            <TableCell>{fertilizerInput.fertilizer_type || '-'}</TableCell>
            <TableCell>{fertilizerInput.nitrogen_content?.toFixed(2) || '-'}</TableCell>
            <TableCell>{fertilizerInput.crop || '-'}</TableCell>
          </>
        )
      case 'nitrogen_stabilized_fertilizer':
        const stabilizedInput = input as any
        return (
          <>
            <TableCell>{stabilizedInput.fertilizer_type || '-'}</TableCell>
            <TableCell>{stabilizedInput.nitrogen_content?.toFixed(2) || '-'}</TableCell>
            <TableCell>{stabilizedInput.stabilizer_type || '-'}</TableCell>
            <TableCell>{stabilizedInput.crop || '-'}</TableCell>
          </>
        )
      case 'controlled_release_fertilizer':
        const crfInput = input as any
        return (
          <>
            <TableCell>{crfInput.fertilizer_type || '-'}</TableCell>
            <TableCell>{crfInput.nitrogen_content?.toFixed(2) || '-'}</TableCell>
            <TableCell>{crfInput.crf_blend_percent?.toFixed(2) || '-'}</TableCell>
            <TableCell>{crfInput.crop || '-'}</TableCell>
          </>
        )
      case 'fuel':
        const fuelInput = input as any
        return (
          <>
            <TableCell>{fuelInput.fuel_type || '-'}</TableCell>
          </>
        )
      default:
        return null
    }
  }

  // Common headers - always display these
  const commonHeaders = (
    <>
      <TableHead>Code</TableHead>
      <TableHead>Name</TableHead>
      <TableHead>Emission Factor</TableHead>
      <TableHead>Unit</TableHead>
      <TableHead>Source</TableHead>
      <TableHead>Year</TableHead>
      <TableHead>Region</TableHead>
      <TableHead>Status</TableHead>
    </>
  )

  // Type-specific header columns
  const getTypeSpecificHeaders = (type: string) => {
    switch(type) {
      case 'low_carbon_fertilizer':
      case 'conventional_fertilizer':
        return (
          <>
            <TableHead>Fertilizer Type</TableHead>
            <TableHead>Nitrogen Content</TableHead>
            <TableHead>Crop</TableHead>
          </>
        )
      case 'nitrogen_stabilized_fertilizer':
        return (
          <>
            <TableHead>Fertilizer Type</TableHead>
            <TableHead>Nitrogen Content</TableHead>
            <TableHead>Stabilizer Type</TableHead>
            <TableHead>Crop</TableHead>
          </>
        )
      case 'controlled_release_fertilizer':
        return (
          <>
            <TableHead>Fertilizer Type</TableHead>
            <TableHead>Nitrogen Content</TableHead>
            <TableHead>CRF Blend %</TableHead>
            <TableHead>Crop</TableHead>
          </>
        )
      case 'fuel':
        return (
          <>
            <TableHead>Fuel Type</TableHead>
          </>
        )
      default:
        return null
    }
  }

  if (isLoading) {
    return <div className="flex justify-center p-4">Loading...</div>
  }

  // Count the number of inputs of each type for tab badges
  const typeCounts = inputTypes.reduce((acc, type) => {
    acc[type] = inputs.filter(input => input.input_type === type).length
    return acc
  }, {} as Record<string, number>)

  // Get section background color based on input type
  const getSectionColor = (type: string) => {
    switch(type) {
      case 'conventional_fertilizer':
        return 'bg-green-50 dark:bg-green-950/20'
      case 'low_carbon_fertilizer':
        return 'bg-yellow-50 dark:bg-yellow-950/20'
      case 'nitrogen_stabilized_fertilizer':
        return 'bg-blue-50 dark:bg-blue-950/20'
      case 'controlled_release_fertilizer':
        return 'bg-purple-50 dark:bg-purple-950/20'
      case 'fuel':
        return 'bg-red-50 dark:bg-red-950/20'
      default:
        return 'bg-muted/50'
    }
  }

  return (
    <div className="rounded-md border bg-white dark:bg-gray-950">
      <div className="p-4 border-b bg-muted/20">
        <Tabs defaultValue="all" onValueChange={setSelectedInputType} className="w-full">
          <TabsList className="">
            <TabsTrigger value="all" className="relative">
              All Types
              <Badge variant="secondary" className="ml-2">{inputs.length}</Badge>
            </TabsTrigger>
            {inputTypes.map(type => (
              <TabsTrigger key={type} value={type} className="relative">
                {inputTypeLabels[type] || type}
                <Badge variant="secondary" className="ml-2">{typeCounts[type]}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Table container with horizontal scroll for many columns */}
      <div className="max-w-full overflow-x-auto pb-4">
        {selectedInputType === 'all' ? (
          // When viewing all types, render each type in its own collapsible section
          <div>
            {Object.entries(groupedInputs).map(([type, typeInputs]) => (
              <Collapsible 
                key={type} 
                open={openSections[type]} 
                onOpenChange={() => toggleSection(type)}
                className="border-b last:border-b-0"
              >
                <CollapsibleTrigger asChild>
                  <div 
                    className={`flex items-center justify-between px-4 py-3 cursor-pointer ${getSectionColor(type)}`}
                  >
                    <div className="flex items-center gap-2">
                      {openSections[type] ? 
                        <ChevronDownIcon className="h-5 w-5" /> : 
                        <ChevronRightIcon className="h-5 w-5" />
                      }
                      <h3 className="text-lg font-medium p-0 m-0">{inputTypeLabels[type] || type}</h3>
                      <Badge variant="outline" className="ml-2">{typeInputs.length}</Badge>
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-2">
                    <Table>
                      <TableHeader className="bg-muted/30">
                        <TableRow>
                          {commonHeaders}
                          {getTypeSpecificHeaders(type)}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {typeInputs.map((input) => (
                          <TableRow 
                            key={input._id} 
                            onClick={() => onRowClick(input)}
                            className="cursor-pointer hover:bg-muted"
                          >
                            {renderCommonColumns(input)}
                            {renderTypeSpecificColumns(input)}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        ) : (
          // When filtering to a specific type, show one table with subtle background color
          <div className={`p-4 ${getSectionColor(selectedInputType)}`}>
            <Table>
              <TableHeader className="bg-white/50 dark:bg-gray-950/50">
                <TableRow>
                  {commonHeaders}
                  {getTypeSpecificHeaders(selectedInputType)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInputs.map((input) => (
                  <TableRow 
                    key={input._id} 
                    onClick={() => onRowClick(input)}
                    className="cursor-pointer hover:bg-muted/50 bg-white/80 dark:bg-gray-950/80"
                  >
                    {renderCommonColumns(input)}
                    {renderTypeSpecificColumns(input)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  )
}

export default InterventionInputTable 